import * as React from 'react';
import {Slot} from '@radix-ui/react-slot';
import {cva, type VariantProps} from 'class-variance-authority';
import {cn} from '../lib/cn';

export const badgeVariants = cva(
  [
    'inline-flex w-fit shrink-0 items-center justify-center gap-1 overflow-hidden whitespace-nowrap',
    'rounded-md border px-2.5 py-0.5 text-xs font-semibold',
    '[&>svg]:pointer-events-none [&>svg]:size-3',
    'transition-[color,box-shadow]',
    'focus-visible:border-[var(--chekin-color-brand-blue)] focus-visible:ring-[3px] focus-visible:ring-[rgba(56,91,248,0.2)]',
    'aria-invalid:border-[var(--chekin-color-brand-red)] aria-invalid:ring-[rgba(255,36,103,0.2)]',
  ],
  {
    variants: {
      variant: {
        default:
          'border-transparent bg-[var(--chekin-color-brand-blue)] text-[var(--chekin-color-white)] [a&]:hover:bg-[var(--chekin-color-brand-blue-hover)]',
        secondary:
          'border-transparent bg-[var(--chekin-color-surface-input-empty)] text-[var(--chekin-color-brand-navy)] [a&]:hover:bg-[color-mix(in_srgb,var(--chekin-color-surface-input-empty)_90%,transparent)]',
        destructive:
          'border-transparent bg-[var(--chekin-color-brand-red)] text-[var(--chekin-color-white)] [a&]:hover:bg-[color-mix(in_srgb,var(--chekin-color-brand-red)_90%,transparent)]',
        outline:
          'border-[var(--chekin-color-gray-3)] text-[var(--chekin-color-brand-navy)] [a&]:hover:bg-[var(--chekin-color-surface-input-empty)]',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  },
);

export interface BadgeProps
  extends React.ComponentProps<'span'>, VariantProps<typeof badgeVariants> {
  asChild?: boolean;
}

export function Badge({className, variant, asChild = false, ...props}: BadgeProps) {
  const Comp = asChild ? Slot : 'span';

  return (
    <Comp
      data-slot="badge"
      className={cn(badgeVariants({variant}), className)}
      {...props}
    />
  );
}

Badge.displayName = 'Badge';
